/**
 * How this estimate carries risk, and how to say so out loud.
 *
 * Contingency is the one number on an estimate that is a percentage of other
 * numbers, which makes it the one number nobody can check by reading the
 * sheet. Two firms using the same rate can be a hundred thousand dollars
 * apart because one applied it to direct cost and the other to the marked-up
 * total, and neither wrote down which. That is an expensive argument to have
 * in a GMP negotiation, so the policy is explicit here and the arithmetic is
 * returned alongside the answer rather than left to be inferred.
 *
 * Three things are separable and are kept separate:
 *
 *   1. WHERE it is held.  A buffer attached to a line item is a judgement
 *      about that scope. A project reserve is a judgement about the project.
 *      Most firms carry one or the other; some carry both, deliberately --
 *      small buffers on the inevitable miscellaneous, plus a reserve the
 *      owner can see and draw against.
 *
 *   2. WHAT it is a percentage OF.  Direct cost, or cost after overhead and
 *      profit. The second is larger by exactly the markup, and is the number
 *      an owner is being asked to approve.
 *
 *   3. WHETHER FEE APPLIES to it.  In most GMP contracts the fee is fixed and
 *      contingency carries none. In cost-plus-a-percentage it may. This is
 *      contested often enough to be worth a switch rather than an assumption.
 *
 * What is deliberately NOT offered: a reserve whose base includes the
 * line-item buffers. That is contingency on contingency, it compounds
 * silently, and no standard supports it.
 */

export type ContingencyMode =
  /** Each line carries its own, at its own rate. The project % is the default
   *  rate a line inherits when it has not been told otherwise. */
  | "line"
  /** One reserve on the project. Line-level settings are suppressed, and the
   *  estimate shows a single figure an owner can draw against. */
  | "reserve"
  /** Both, additively: buffers where the scope is soft, and a reserve on top
   *  for what no line anticipated. */
  | "both";

export type ReserveBase =
  /** Direct cost, before overhead and profit. The conservative reading, and
   *  the usual one in public work. */
  | "direct"
  /** Cost after overhead and profit -- the number being approved. A reserve
   *  sized against direct cost under-covers the exposure by the markup. */
  | "withMarkup";

export type ContingencyPolicy = {
  mode: ContingencyMode;
  /** The project reserve rate, used only when the mode includes a reserve.
   *  Distinct from the per-line default rate, which lives on the estimate as
   *  `contingency` -- keeping them apart is what stops "both" from charging
   *  the same percentage twice under two names. */
  reservePct: number;
  base: ReserveBase;
  /** Whether overhead and profit are charged on line-item contingency. Never
   *  applies to the reserve: a reserve on the marked-up total has already
   *  accounted for markup, and one on direct cost was chosen not to. */
  feeOnLineContingency: boolean;
};

export const DEFAULT_CONTINGENCY_POLICY: ContingencyPolicy = {
  mode: "line",
  reservePct: 0,
  base: "direct",
  feeOnLineContingency: false,
};

/** One line of the arithmetic, in the order it happens. */
export type ContingencyStep = {
  label: string;
  /** Why this line exists, in a sentence an owner would accept. */
  detail: string;
  /** The sum actually performed, with this estimate's own numbers in it. */
  formula: string;
  amount: number;
};

export type ContingencyResult = {
  /** Line-item buffers, after the mode has had its say. */
  lineTotal: number;
  /** The project reserve. */
  reserve: number;
  /** What lands in the totals stack. */
  total: number;
  steps: ContingencyStep[];
};

const usesLines   = (m: ContingencyMode) => m === "line" || m === "both";
const usesReserve = (m: ContingencyMode) => m === "reserve" || m === "both";

/** Line-item contingency is live under this policy. The estimate grid reads
 *  this to decide whether to grey its own contingency column out, so a user
 *  in reserve mode is not quietly editing a number that does not count. */
export const lineContingencyApplies = (p: ContingencyPolicy) => usesLines(p.mode);

/** The part of contingency that overhead and profit are charged on. Needed
 *  before markup is computed, which is why it is separate from the rest. */
export function feeBearingContingency(
  policy: ContingencyPolicy,
  lineContingency: number,
): number {
  if (!usesLines(policy.mode) || !policy.feeOnLineContingency) return 0;
  return lineContingency;
}

/**
 * What to call the project-level figure, which depends on what else is on
 * screen beside it.
 *
 * When it is the only contingency the estimate carries, it is the
 * contingency, and "project reserve" would be jargon for a distinction the
 * reader cannot see. It earns the longer name only in "both", where a line
 * total sits directly above it and the two have to be told apart.
 *
 * The base is always named, in either case: that is the part that cannot be
 * recovered from the sheet.
 */
export function reserveLabel(p: ContingencyPolicy): string {
  const base = p.base === "withMarkup" ? "cost + markup" : "direct cost";
  const name = p.mode === "both" ? "Project reserve" : "Contingency";
  return `${name} (${p.reservePct}% of ${base})`;
}

const money = (n: number) =>
  `$${Math.round(n).toLocaleString("en-US")}`;
const pct = (n: number) =>
  `${Number.isInteger(n) ? n : n.toFixed(2)}%`;

/**
 * Resolve the policy against this estimate, and show the working.
 *
 * `overhead` and `profit` are dollars already computed, because the reserve
 * may be a percentage of them. They must have been computed with
 * `feeBearingContingency` folded in, or the two halves disagree.
 */
export function resolveContingency(input: {
  policy: ContingencyPolicy;
  /** Sum of every line's own contingency, as the grid computed it. */
  lineContingency: number;
  directSubtotal: number;
  overhead: number;
  profit: number;
}): ContingencyResult {
  const { policy, lineContingency, directSubtotal, overhead, profit } = input;
  const steps: ContingencyStep[] = [];

  const lineTotal = usesLines(policy.mode) ? lineContingency : 0;
  if (usesLines(policy.mode)) {
    steps.push({
      label: "Line-item contingency",
      detail:
        "Each line's own buffer, at the rate set on that line. A line left on the project default uses that rate; a line set to Custom replaces it rather than adding to it.",
      formula: "sum of every line's contingency",
      amount: lineTotal,
    });
    if (policy.feeOnLineContingency) {
      steps.push({
        label: "Fee on line-item contingency",
        detail:
          "Overhead and profit are charged on the buffers, so they are inside the marked-up subtotal above rather than added here.",
        formula: `${money(lineTotal)} is included in the base for overhead and profit`,
        amount: 0,
      });
    }
  } else if (lineContingency > 0) {
    steps.push({
      label: "Line-item contingency — not counted",
      detail:
        "This estimate carries a single project reserve, so per-line buffers are held but not added. Switch to Line-item or Both to count them.",
      formula: `${money(lineContingency)} set on lines, excluded by the current mode`,
      amount: 0,
    });
  }

  let reserve = 0;
  if (usesReserve(policy.mode)) {
    const base =
      policy.base === "withMarkup" ? directSubtotal + overhead + profit : directSubtotal;
    reserve = base * (policy.reservePct / 100);
    steps.push({
      label: policy.mode === "both" ? "Project reserve" : "Contingency",
      detail:
        policy.base === "withMarkup"
          ? "A percentage of the cost after overhead and profit — the figure being approved. Line-item buffers are not in this base, so the reserve is never a percentage of contingency."
          : "A percentage of direct cost, before overhead and profit. Line-item buffers are not in this base, so the reserve is never a percentage of contingency.",
      formula: `${pct(policy.reservePct)} × ${money(base)}${
        policy.base === "withMarkup" ? " (subtotal + overhead + profit)" : " (direct cost subtotal)"
      }`,
      amount: reserve,
    });
  }

  const total = lineTotal + reserve;
  if (steps.length > 1) {
    steps.push({
      label: "Total contingency",
      detail: "Added after overhead and profit, and carried as its own line in the total.",
      formula: steps
        .filter((s) => s.amount !== 0)
        .map((s) => money(s.amount))
        .join(" + ") || money(0),
      amount: total,
    });
  }

  return { lineTotal, reserve, total, steps };
}

/**
 * What the industry actually does, for the panel that explains the choice.
 *
 * Ranges are the ones an estimator would recognise rather than a citation:
 * design contingency falls as the drawings firm up, construction contingency
 * is sized against the risk of the work, and escalation is not contingency at
 * all and is the most common thing to find hidden inside it.
 */
export const CONTINGENCY_GUIDANCE: Array<{ kind: string; typical: string; note: string }> = [
  {
    kind: "Design / estimating contingency",
    typical: "15–20% concept · 10–15% SD · 7–10% DD · 3–5% CD · 1–2% at bid",
    note: "Covers scope the drawings do not show yet. It should fall at every milestone, and an estimate that carries the same rate from concept to CDs is not being re-examined.",
  },
  {
    kind: "Construction contingency (GC / CM)",
    typical: "1.5–5% of the cost of work",
    note: "Inside the GMP and controlled by the CM: buyout shortfalls, sub default, coordination gaps. Not for owner-directed changes — those are change orders, and spending contingency on them hides a scope increase.",
  },
  {
    kind: "Owner's contingency",
    typical: "5–10% of construction cost",
    note: "Held by the owner, usually outside the GMP, for owner-directed scope and unforeseen conditions. Keeping it out of the contractor's number is what makes the contractor's number auditable.",
  },
  {
    kind: "Escalation",
    typical: "Priced to the schedule midpoint, not a flat percentage",
    note: "Not contingency. It is a known cost with an unknown amount, and folding it into contingency means the reserve is spent before the first unknown arrives.",
  },
  {
    kind: "Allowances",
    typical: "Priced per item",
    note: "Also not contingency. An allowance is known scope at an unknown price; contingency is unknown scope. Carrying contingency on top of an allowance that already holds a buffer double-counts the same risk.",
  },
];
